import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useBeds, useWards, useAdmissions, usePatient } from '../../services/httpServices/queries';
import { joinFullName, patientInitials } from '../../utils/patientName';
import {
  BedDouble,
  Loader2,
  AlertCircle,
  CheckCircle,
  Users,
  Wrench,
  Bookmark,
  Search,
  ArrowLeft,
  LayoutGrid,
  BarChart3,
  X,
  MapPin,
  ClipboardList,
  Banknote,
  Hash,
  Stethoscope,
  User,
  FileText,
  Calendar,
  Activity,
  Phone,
  Droplets,
  Cake,
  AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';
import { BedFloorPlan } from '../../components/clinical/BedFloorPlan';

const STATUS_META: Record<string, { label: string; color: string; bg: string; border: string; dot: string; hex: string }> = {
  AVAILABLE: {
    label: 'Available',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
    hex: '#10b981',
  },
  OCCUPIED: {
    label: 'Occupied',
    color: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    dot: 'bg-rose-500',
    hex: '#f43f5e',
  },
  RESERVED: {
    label: 'Reserved',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
    hex: '#f59e0b',
  },
  MAINTENANCE: {
    label: 'Maintenance',
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    border: 'border-slate-200',
    dot: 'bg-slate-400',
    hex: '#94a3b8',
  },
};

/** Admission statuses that represent a currently admitted patient. */
const ACTIVE_ADMISSION_STATUSES = new Set(['ADMITTED', 'DISCHARGE_INITIATED']);

function capitalize(value?: string | null): string {
  if (!value) return 'N/A';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function formatDate(value?: string | null): string {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
}

function getInitials(patient: any): string {
  if (!patient) return '?';
  return patientInitials(patient) || patient.registrationNumber?.slice(0, 2)?.toUpperCase() || '?';
}

function DetailTile({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[0.625rem] text-text-muted uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-text truncate">{value}</p>
      </div>
    </div>
  );
}

export function BedOccupancy() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'graphical' | 'summary'>('graphical');
  const [selectedBed, setSelectedBed] = useState<any | null>(null);

  const { data: bedsData, isLoading: bedsLoading, error: bedsError } = useBeds();
  const { data: wardsData, isLoading: wardsLoading, error: wardsError } = useWards(1, 100);
  const { data: admissionsData, isLoading: admissionsLoading } = useAdmissions();

  const beds = bedsData?.data || [];
  const wards = wardsData?.data || [];
  const admissions = admissionsData?.data || [];

  const bedAdmissionMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const a of admissions) {
      if (!a.bedId) continue;
      const existing = map.get(a.bedId);
      if (!existing) {
        map.set(a.bedId, a);
        continue;
      }
      // Prefer the currently active admission; otherwise keep the most recent one.
      const aActive = ACTIVE_ADMISSION_STATUSES.has(a.status);
      const existingActive = ACTIVE_ADMISSION_STATUSES.has(existing.status);
      const aTime = new Date(a.admittedAt || a.createdAt || 0).getTime();
      const existingTime = new Date(existing.admittedAt || existing.createdAt || 0).getTime();
      if ((aActive && !existingActive) || (aActive === existingActive && aTime > existingTime)) {
        map.set(a.bedId, a);
      }
    }
    return map;
  }, [admissions]);

  const stats = useMemo(() => {
    const total = beds.length;
    const occupied = beds.filter((b: any) => b.status === 'OCCUPIED').length;
    const available = beds.filter((b: any) => b.status === 'AVAILABLE').length;
    const reserved = beds.filter((b: any) => b.status === 'RESERVED').length;
    const maintenance = beds.filter((b: any) => b.status === 'MAINTENANCE').length;
    const occupancyRate = total > 0 ? Math.round((occupied / total) * 100) : 0;
    return { total, occupied, available, reserved, maintenance, occupancyRate };
  }, [beds]);

  const chartData = useMemo(() => {
    return [
      { name: 'Available', value: stats.available, color: STATUS_META.AVAILABLE.hex },
      { name: 'Occupied', value: stats.occupied, color: STATUS_META.OCCUPIED.hex },
      { name: 'Reserved', value: stats.reserved, color: STATUS_META.RESERVED.hex },
      { name: 'Maintenance', value: stats.maintenance, color: STATUS_META.MAINTENANCE.hex },
    ].filter((d) => d.value > 0);
  }, [stats]);

  const graphicalBeds = useMemo(() => {
    return beds.map((bed: any) => {
      const admission = bedAdmissionMap.get(bed.id);
      return {
        id: bed.id,
        bedNumber: bed.bedNumber,
        ward: bed.ward || 'Unassigned',
        status: bed.status,
        type: bed.type,
        price: bed.price,
        patientName: admission?.patient
          ? joinFullName(admission.patient)
          : undefined,
      };
    });
  }, [beds, bedAdmissionMap]);

  const graphicalWards = useMemo(() => {
    return wards.map((w: any) => ({
      id: w.id,
      wardCode: w.wardCode,
      wardName: w.wardName,
      wardType: w.wardType,
      floor: w.floor,
      totalBeds: w.totalBeds,
    }));
  }, [wards]);

  const isLoading = bedsLoading || wardsLoading || admissionsLoading;
  const error = bedsError || wardsError;

  // Resolve the currently occupying patient for the modal.
  const selectedAdmission = selectedBed ? bedAdmissionMap.get(selectedBed.id) || null : null;
  const isOccupied = selectedBed?.status === 'OCCUPIED';
  const showOccupancy = isOccupied || !!selectedAdmission;
  const admissionIsActive = !!selectedAdmission && ACTIVE_ADMISSION_STATUSES.has(selectedAdmission.status);
  const admissionPatient = admissionIsActive ? selectedAdmission?.patient ?? null : null;
  const currentPatientId: string | undefined = selectedBed?.currentPatientId || selectedAdmission?.patientId;
  // Only fetch the patient record when the admission itself doesn't carry one
  // (e.g. an occupied bed whose admission was already discharged).
  const shouldFetchPatient = isOccupied && !admissionPatient && !!currentPatientId;
  const { data: fetchedPatient, isLoading: patientLoading } = usePatient(
    shouldFetchPatient ? currentPatientId! : '',
  );
  const occupancyPatient = admissionPatient || fetchedPatient?.data || selectedAdmission?.patient || null;

  const admissionStatusClass = !selectedAdmission
    ? 'bg-amber-100 text-amber-700'
    : selectedAdmission.status === 'ADMITTED'
      ? 'bg-primary/10 text-primary'
      : selectedAdmission.status === 'DISCHARGED'
        ? 'bg-emerald-100 text-emerald-700'
        : 'bg-amber-100 text-amber-700';

  const alertFlags = ([
    occupancyPatient?.highRisk && { label: 'High Risk', className: 'bg-rose-50 text-rose-700 border-rose-200' },
    occupancyPatient?.drugAllergy && { label: 'Drug Allergy', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    occupancyPatient?.infectiousDisease && { label: 'Infectious', className: 'bg-purple-50 text-purple-700 border-purple-200' },
    occupancyPatient?.fallRisk && { label: 'Fall Risk', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  ].filter(Boolean) as { label: string; className: string }[]);

  const getPatientName = (patient: any) => {
    if (!patient) return 'Unknown';
    return joinFullName(patient) || patient.registrationNumber || 'Unknown';
  };

  const getDoctorName = (doctor: any) => doctor?.name || 'Unknown';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/ipd')}
            className="w-9 h-9 rounded-xl bg-background border border-border flex items-center justify-center hover:bg-surface transition-colors"
            title="Back to IPD"
          >
            <ArrowLeft size={18} className="text-text-muted" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-text tracking-tight">Bed Occupancy Overview</h1>
            <p className="text-sm text-text-muted">Graphical view of ward and bed occupancy</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center bg-surface border border-border rounded-xl p-1 shadow-sm">
            <button
              onClick={() => setViewMode('graphical')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                viewMode === 'graphical'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              <LayoutGrid size={15} />
              Floor Plan
            </button>
            <button
              onClick={() => setViewMode('summary')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                viewMode === 'summary'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              <BarChart3 size={15} />
              Summary
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-xl text-danger">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">Failed to load data</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Top Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-surface border border-border rounded-xl p-4 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <BedDouble size={22} className="text-primary" />
              </div>
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Total Beds</p>
                <p className="text-2xl font-bold text-text">{stats.total}</p>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                <Users size={22} className="text-rose-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Occupied</p>
                <p className="text-2xl font-bold text-rose-600">{stats.occupied}</p>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <CheckCircle size={22} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Available</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.available}</p>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <Bookmark size={22} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Reserved</p>
                <p className="text-2xl font-bold text-amber-600">{stats.reserved}</p>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <Wrench size={22} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Maintenance</p>
                <p className="text-2xl font-bold text-slate-600">{stats.maintenance}</p>
              </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <Activity size={22} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Occupancy</p>
                <p className="text-2xl font-bold text-blue-600">{stats.occupancyRate}%</p>
              </div>
            </div>
          </div>

          {viewMode === 'graphical' ? (
            <>
              {/* Search for graphical view */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Filter wards..."
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* PixiJS Floor Plan */}
              <BedFloorPlan
                beds={graphicalBeds}
                wards={graphicalWards}
                filterWard={searchQuery}
                onBedClick={(bed) => {
                  const fullBed = beds.find((b: any) => b.id === bed.id);
                  if (fullBed) setSelectedBed(fullBed);
                }}
              />
            </>
          ) : (
            <>
              {/* Chart + Ward Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-surface border border-border rounded-2xl p-6 shadow-sm flex flex-col items-center justify-center">
                  <h3 className="text-sm font-bold text-text-muted uppercase tracking-wider mb-4">Overall Distribution</h3>
                  <div className="w-full h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                          stroke="none"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <ReTooltip
                          formatter={(value: number, name: string) => [`${value} beds`, name]}
                          contentStyle={{
                            borderRadius: 12,
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            fontSize: 13,
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap justify-center gap-4 mt-2">
                    {chartData.map((d) => (
                      <div key={d.name} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className="text-xs text-text-muted font-medium">{d.name}</span>
                        <span className="text-xs font-bold text-text">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-2 bg-surface border border-border rounded-2xl p-6 shadow-sm">
                  <h3 className="text-sm font-bold text-text-muted uppercase tracking-wider mb-4">Ward Summary</h3>
                  <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
                    {(() => {
                      const bedsByWard = new Map<string, any[]>();
                      for (const bed of beds) {
                        const wn = bed.ward || 'Unassigned';
                        if (!bedsByWard.has(wn)) bedsByWard.set(wn, []);
                        bedsByWard.get(wn)!.push(bed);
                      }
                      const entries = Array.from(bedsByWard.entries()).sort((a, b) => a[0].localeCompare(b[0]));
                      return entries.map(([wardName, wardBeds]) => {
                        const wardInfo = wards.find((w: any) => w.wardName === wardName);
                        const occupiedCount = wardBeds.filter((b: any) => b.status === 'OCCUPIED').length;
                        const availableCount = wardBeds.filter((b: any) => b.status === 'AVAILABLE').length;
                        const reservedCount = wardBeds.filter((b: any) => b.status === 'RESERVED').length;
                        const maintCount = wardBeds.filter((b: any) => b.status === 'MAINTENANCE').length;
                        const rate = wardBeds.length > 0 ? Math.round((occupiedCount / wardBeds.length) * 100) : 0;
                        return (
                          <div key={wardName} className="flex items-center gap-4">
                            <div className="w-40 shrink-0">
                              <p className="text-sm font-semibold text-text truncate">{wardName}</p>
                              <p className="text-[0.6875rem] text-text-muted">{wardInfo?.floor ? `Floor ${wardInfo.floor}` : '—'} · {wardBeds.length} beds</p>
                            </div>
                            <div className="flex-1">
                              <div className="w-full h-3 rounded-full bg-background overflow-hidden flex">
                                <div className="h-full bg-emerald-500" style={{ width: `${(availableCount / wardBeds.length) * 100}%` }} />
                                <div className="h-full bg-rose-500" style={{ width: `${(occupiedCount / wardBeds.length) * 100}%` }} />
                                <div className="h-full bg-amber-400" style={{ width: `${(reservedCount / wardBeds.length) * 100}%` }} />
                                <div className="h-full bg-slate-300" style={{ width: `${(maintCount / wardBeds.length) * 100}%` }} />
                              </div>
                            </div>
                            <div className="w-12 text-right">
                              <span className="text-sm font-bold text-text">{rate}%</span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              {/* Ward Bed Dot Maps */}
              <div>
                <h3 className="text-sm font-bold text-text-muted uppercase tracking-wider mb-4">Ward Bed Maps</h3>
                {(() => {
                  const bedsByWard = new Map<string, any[]>();
                  for (const bed of beds) {
                    const wn = bed.ward || 'Unassigned';
                    if (!bedsByWard.has(wn)) bedsByWard.set(wn, []);
                    bedsByWard.get(wn)!.push(bed);
                  }
                  const entries = Array.from(bedsByWard.entries()).sort((a, b) => a[0].localeCompare(b[0]));
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {entries.map(([wardName, wardBeds]) => {
                        const wardInfo = wards.find((w: any) => w.wardName === wardName);
                        const occupiedCount = wardBeds.filter((b: any) => b.status === 'OCCUPIED').length;
                        const availableCount = wardBeds.filter((b: any) => b.status === 'AVAILABLE').length;
                        const reservedCount = wardBeds.filter((b: any) => b.status === 'RESERVED').length;
                        const maintCount = wardBeds.filter((b: any) => b.status === 'MAINTENANCE').length;
                        const rate = wardBeds.length > 0 ? Math.round((occupiedCount / wardBeds.length) * 100) : 0;
                        return (
                          <div key={wardName} className="bg-surface border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h2 className="text-base font-bold text-text">{wardName}</h2>
                                <p className="text-xs text-text-muted mt-0.5">
                                  {wardInfo?.wardType?.replace('_', ' ')} · {wardInfo?.floor ? `Floor ${wardInfo.floor}` : 'No floor'} · {wardBeds.length} beds
                                </p>
                              </div>
                              <div className={`px-2.5 py-1 rounded-lg text-xs font-bold ${rate >= 90 ? 'bg-rose-100 text-rose-700' : rate >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {rate}% full
                              </div>
                            </div>
                            <div className="w-full h-2.5 rounded-full bg-background overflow-hidden flex mb-4">
                              <div className="h-full bg-emerald-500" style={{ width: `${(availableCount / wardBeds.length) * 100}%` }} />
                              <div className="h-full bg-rose-500" style={{ width: `${(occupiedCount / wardBeds.length) * 100}%` }} />
                              <div className="h-full bg-amber-400" style={{ width: `${(reservedCount / wardBeds.length) * 100}%` }} />
                              <div className="h-full bg-slate-300" style={{ width: `${(maintCount / wardBeds.length) * 100}%` }} />
                            </div>
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              {wardBeds.map((bed: any) => {
                                const meta = STATUS_META[bed.status] || STATUS_META.AVAILABLE;
                                const admission = bedAdmissionMap.get(bed.id);
                                return (
                                  <button
                                    key={bed.id}
                                    onClick={() => setSelectedBed(bed)}
                                    title={`${bed.bedNumber} — ${meta.label}${admission ? ` (${getPatientName(admission.patient)})` : ''}`}
                                    className={`w-6 h-6 rounded-md ${meta.dot} opacity-90 hover:opacity-100 hover:scale-110 transition-all cursor-pointer`}
                                  />
                                );
                              })}
                            </div>
                            <div className="flex flex-wrap gap-3 pt-3 border-t border-border">
                              {availableCount > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                  <span className="text-[0.6875rem] text-text-muted font-medium">{availableCount} Available</span>
                                </div>
                              )}
                              {occupiedCount > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                                  <span className="text-[0.6875rem] text-text-muted font-medium">{occupiedCount} Occupied</span>
                                </div>
                              )}
                              {reservedCount > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                  <span className="text-[0.6875rem] text-text-muted font-medium">{reservedCount} Reserved</span>
                                </div>
                              )}
                              {maintCount > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2.5 h-2.5 rounded-full bg-slate-400" />
                                  <span className="text-[0.6875rem] text-text-muted font-medium">{maintCount} Maint.</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </>
      )}

      {/* ===== BED DETAIL MODAL ===== */}
      {selectedBed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setSelectedBed(null)}>
          <div
            className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative overflow-hidden">
              <div className={`h-24 ${(STATUS_META[selectedBed.status] || STATUS_META.AVAILABLE).dot.replace('bg-', 'bg-').replace('500', '100')} opacity-20`} />
              <div className="absolute inset-0 flex items-end p-5">
                <div className="flex items-center gap-4 w-full">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm border-2 border-white ${(STATUS_META[selectedBed.status] || STATUS_META.AVAILABLE).bg}`}>
                    <BedDouble size={26} className={(STATUS_META[selectedBed.status] || STATUS_META.AVAILABLE).color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-text truncate">Bed {selectedBed.bedNumber}</h2>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[0.625rem] font-bold uppercase tracking-wider ${
                        selectedBed.status === 'AVAILABLE' ? 'bg-emerald-100 text-emerald-700' :
                        selectedBed.status === 'OCCUPIED' ? 'bg-rose-100 text-rose-700' :
                        selectedBed.status === 'RESERVED' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {(STATUS_META[selectedBed.status] || STATUS_META.AVAILABLE).label}
                      </span>
                      <span className="text-xs text-text-muted">{selectedBed.ward}</span>
                    </div>
                  </div>
                  <button onClick={() => setSelectedBed(null)} className="p-2 hover:bg-black/5 rounded-xl transition-colors">
                    <X size={18} className="text-text-muted" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-3">Bed Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <MapPin size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[0.625rem] text-text-muted uppercase tracking-wide">Ward</p>
                      <p className="text-sm font-semibold text-text truncate">{selectedBed.ward || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <ClipboardList size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[0.625rem] text-text-muted uppercase tracking-wide">Type</p>
                      <p className="text-sm font-semibold text-text truncate">{selectedBed.type || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Banknote size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[0.625rem] text-text-muted uppercase tracking-wide">Price</p>
                      <p className="text-sm font-semibold text-text">৳{selectedBed.price || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Hash size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[0.625rem] text-text-muted uppercase tracking-wide">ID</p>
                      <p className="text-sm font-semibold text-text font-mono">{selectedBed.id.slice(0, 8)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {showOccupancy ? (
                <div className="border-t border-border pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">
                      {isOccupied ? 'Patient Details' : 'Current Occupancy'}
                    </h3>
                    {selectedAdmission && (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[0.625rem] font-bold uppercase tracking-wide ${admissionStatusClass}`}>
                        {selectedAdmission.status}
                      </span>
                    )}
                  </div>

                  {patientLoading && !occupancyPatient ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : occupancyPatient ? (
                    <div className="space-y-3">
                      {/* Patient identity */}
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15">
                        <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-bold">
                          {getInitials(occupancyPatient)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-text truncate">{getPatientName(occupancyPatient)}</p>
                          <p className="text-[0.6875rem] text-text-muted font-mono truncate">
                            {occupancyPatient.registrationNumber || 'No registration number'}
                          </p>
                        </div>
                      </div>

                      {/* Clinical alert flags */}
                      {alertFlags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {alertFlags.map((flag) => (
                            <span
                              key={flag.label}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[0.625rem] font-bold border ${flag.className}`}
                            >
                              <AlertTriangle size={11} /> {flag.label}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Demographics */}
                      <div className="grid grid-cols-2 gap-3">
                        <DetailTile
                          icon={<User size={15} />}
                          label="Age / Gender"
                          value={`${occupancyPatient.age ?? '—'} yrs · ${capitalize(occupancyPatient.gender)}`}
                        />
                        <DetailTile
                          icon={<Droplets size={15} />}
                          label="Blood Group"
                          value={occupancyPatient.bloodGroup || 'N/A'}
                        />
                        <DetailTile
                          icon={<Phone size={15} />}
                          label="Mobile"
                          value={occupancyPatient.mobileNumber || 'N/A'}
                        />
                        <DetailTile
                          icon={<Cake size={15} />}
                          label="Marital Status"
                          value={capitalize(occupancyPatient.maritalStatus)}
                        />
                      </div>

                      {(occupancyPatient.permFullAddress || occupancyPatient.presFullAddress) && (
                        <DetailTile
                          icon={<MapPin size={15} />}
                          label="Address"
                          value={occupancyPatient.permFullAddress || occupancyPatient.presFullAddress}
                        />
                      )}

                      {occupancyPatient.emergencyContactName && (
                        <DetailTile
                          icon={<Phone size={15} />}
                          label="Emergency Contact"
                          value={`${occupancyPatient.emergencyContactName}${
                            occupancyPatient.emergencyContactRelation ? ` (${occupancyPatient.emergencyContactRelation})` : ''
                          }${occupancyPatient.emergencyContactPhone ? ` · ${occupancyPatient.emergencyContactPhone}` : ''}`}
                        />
                      )}

                      {/* Admission context */}
                      {selectedAdmission && (
                        <div className="pt-3 mt-1 border-t border-dashed border-border space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <DetailTile
                              icon={<Stethoscope size={15} />}
                              label="Doctor"
                              value={getDoctorName(selectedAdmission.doctor)}
                            />
                            <DetailTile
                              icon={<ClipboardList size={15} />}
                              label="Admission Type"
                              value={capitalize(selectedAdmission.admissionType)}
                            />
                          </div>
                          <DetailTile
                            icon={<FileText size={15} />}
                            label="Diagnosis"
                            value={selectedAdmission.diagnosis || 'N/A'}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <DetailTile
                              icon={<Calendar size={15} />}
                              label="Admitted"
                              value={formatDate(selectedAdmission.admittedAt || selectedAdmission.admissionDate || selectedAdmission.createdAt)}
                            />
                            <DetailTile
                              icon={<Activity size={15} />}
                              label="Admission Status"
                              value={
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[0.625rem] font-bold uppercase tracking-wide ${admissionStatusClass}`}>
                                  {selectedAdmission.status}
                                </span>
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                        <AlertCircle size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-amber-800">No patient record linked</p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          This bed is marked occupied but no active admission or patient record was found.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-t border-border pt-5">
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                      <CheckCircle size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-800">Bed Available</p>
                      <p className="text-xs text-emerald-600 mt-0.5">This bed is vacant and ready for a new admission.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
